import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, PoundSterling, TrendingUp, TrendingDown, Trash2, Mail, Percent, Edit, Download, FileDown, Upload, FileText, Search, X, RotateCcw, Archive } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BulkDownloadForm = ({ selectedArtist }) => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [downloading, setDownloading] = useState(false);

  const handleBulkDownload = async () => {
    try {
      setDownloading(true);
      const token = localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedArtist && selectedArtist !== 'all') params.append('artist_id', selectedArtist);
      
      const response = await axios.get(`${API}/invoices/bulk-download/pdf?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      let filename = 'invoices';
      if (startDate) filename += `_from_${startDate}`;
      if (endDate) filename += `_to_${endDate}`;
      filename += '.zip';
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Invoices downloaded successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download invoices');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="start-date">Start Date (Optional)</Label>
        <Input
          id="start-date"
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="bg-input/50 border-border rounded-xl"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="end-date">End Date (Optional)</Label>
        <Input
          id="end-date"
          type="date"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          className="bg-input/50 border-border rounded-xl"
        />
      </div>
      <p className="text-sm text-muted-foreground">
        Leave dates empty to download all invoices{selectedArtist && selectedArtist !== 'all' ? ' for the selected artist' : ''}.
      </p>
      <Button
        onClick={handleBulkDownload}
        disabled={downloading}
        className="w-full bg-primary hover:bg-primary/90"
      >
        <Download className="w-4 h-4 mr-2" />
        {downloading ? 'Preparing Download...' : 'Download as ZIP'}
      </Button>
    </div>
  );
};

const Invoices = ({ user, selectedArtist }) => {
  const [invoices, setInvoices] = useState([]);
  const [trashedInvoices, setTrashedInvoices] = useState([]);
  const [viewingTrash, setViewingTrash] = useState(false);
  const [summary, setSummary] = useState({ total_income: 0, total_expenses: 0, balance: 0, invoice_count: 0 });
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [companySettings, setCompanySettings] = useState(null);
  const [formData, setFormData] = useState({
    invoice_number: '',
    artist_id: '',
    invoice_to_type: 'artist',
    type: 'income',
    amount: '',
    currency: 'GBP',
    description: '',
    date: '',
    status: 'pending',
    from_entity_type: 'management',
    from_entity_id: '',
    recipient_name: '',
    recipient_company: '',
    recipient_email: '',
    recipient_address: '',
  });
  const [includeVat, setIncludeVat] = useState(false); // VAT checkbox - default off, user can enable
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Currency options
  const currencies = [
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'CHF', symbol: 'CHF', name: 'Swiss Franc' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
  ];

  const getCurrencySymbol = (code) => {
    const currency = currencies.find(c => c.code === code);
    return currency ? currency.symbol : '£';
  };

  const [bulkImportImage, setBulkImportImage] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkImportType, setBulkImportType] = useState('prs');
  const [commissionRate, setCommissionRate] = useState(20);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'paid', 'overdue'
  const [sourceFilter, setSourceFilter] = useState('all'); // 'all', 'prs', 'ppl', 'commission', 'other'
  const [bulkImportTab, setBulkImportTab] = useState('image'); // 'image' or 'text'
  const [bulkImportText, setBulkImportText] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // Search invoices
  const [emailDestination, setEmailDestination] = useState('artist'); // 'artist' or 'me'

  // Determine effective invoice type based on viewing perspective
  // Commission invoices FROM management TO artists:
  // - Show as INCOME when viewing as Management (Underplay is receiving commission)
  // - Show as EXPENSE when viewing as that Artist (Artist is paying commission)
  const getEffectiveType = (invoice) => {
    // If viewing as Management
    if (selectedArtist === 'management') {
      // Commission invoices from management are income for management
      if (invoice.from_entity_type === 'management' && invoice.invoice_to_type === 'artist') {
        return 'income';
      }
      return invoice.type;
    }
    
    // If viewing as a specific Artist
    if (selectedArtist && selectedArtist !== 'management') {
      // Commission invoices TO this artist are expenses for them
      if (invoice.from_entity_type === 'management' && invoice.artist_id === selectedArtist) {
        return 'expense';
      }
      return invoice.type;
    }
    
    // Default: use original type
    return invoice.type;
  };

  // Helper to check if user has manager-level access
  const isManager = user?.role === 'manager' || user?.role === 'admin';

  const fetchSettings = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanySettings(response.data);
    } catch (error) {
      
      // Settings not found - use defaults
    }
  }, []);

  const fetchSettingsForArtist = async (artistId) => {
    if (!artistId) return null;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings?user_id=${artistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return response.data;
    } catch (error) {
      // Settings not found for artist
      return null;
    }
  };

  const generateInvoiceNumber = useCallback(() => {
    const now = new Date();
    const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    setFormData(prev => ({ ...prev, invoice_number: `INV-${dateStr}-${random}` }));
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (selectedArtist === 'management') {
        queryParams.append('filter_type', 'management');
      } else if (selectedArtist) {
        queryParams.append('artist_id', selectedArtist);
      }
      const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await axios.get(`${API}/invoices${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(response.data);
    } catch (error) {
      toast.error('Failed to fetch invoices');
    }
  }, [selectedArtist]);

  const fetchTrashedInvoices = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/invoices/trash`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTrashedInvoices(response.data);
    } catch (error) {
      
      // Silent fail - trash is supplementary
    }
  }, []);

  const restoreInvoice = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/invoices/${invoiceId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Invoice restored successfully');
      fetchTrashedInvoices();
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore invoice');
    }
  };

  const permanentlyDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to permanently delete this invoice? This action cannot be undone.')) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/invoices/${invoiceId}/permanent`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Invoice permanently deleted');
      fetchTrashedInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const fetchSummary = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams();
      if (selectedArtist === 'management') {
        queryParams.append('filter_type', 'management');
      } else if (selectedArtist) {
        queryParams.append('artist_id', selectedArtist);
      }
      const params = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await axios.get(`${API}/invoices/summary${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(response.data);
    } catch (error) {
      
      // Silent fail - summary is supplementary
    }
  }, [selectedArtist]);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      
      // Silent fail - artists list is supplementary
    }
  }, []);

  // Main data loading effect - must be after all useCallback definitions
  useEffect(() => {
    fetchInvoices();
    fetchSummary();
    fetchSettings();
    if (isManager) {
      fetchArtists();
      fetchTrashedInvoices();
    }
    generateInvoiceNumber();
  }, [selectedArtist, fetchInvoices, fetchSummary, fetchSettings, fetchArtists, fetchTrashedInvoices, generateInvoiceNumber, isManager]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isManager) {
      toast.error('Only managers can create invoices');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Determine artist_id - use the artist from either FROM or TO fields
      let artistId = formData.artist_id;
      if (!artistId && formData.from_entity_type === 'artist' && formData.from_entity_id) {
        // If invoice is FROM an artist, use that artist's ID
        artistId = formData.from_entity_id;
      }
      
      // Calculate VAT if enabled
      const netAmount = parseFloat(formData.amount);
      let finalAmount = netAmount;
      let vatAmount = 0;
      
      if (includeVat && netAmount > 0) {
        vatAmount = netAmount * 0.2; // 20% VAT
        finalAmount = netAmount + vatAmount;
      }
      
      // Validate date
      const dateValue = formData.date ? new Date(formData.date) : new Date();
      if (isNaN(dateValue.getTime())) {
        toast.error('Please enter a valid date');
        return;
      }
      
      const payload = {
        invoice_number: formData.invoice_number,
        artist_id: artistId || null,
        type: formData.type,
        amount: finalAmount,
        currency: formData.currency,
        description: formData.description || null,
        date: dateValue.toISOString(),
        status: formData.status,
        from_entity_type: formData.from_entity_type,
        from_entity_id: formData.from_entity_id || null,
        invoice_to_type: formData.invoice_to_type,
        // VAT tracking fields - ensure boolean type
        vat_included: Boolean(includeVat),
        net_amount: includeVat ? netAmount : null,
        vat_amount: includeVat ? vatAmount : null,
        // Recipient fields - convert empty strings to null
        recipient_name: formData.recipient_name || null,
        recipient_company: formData.recipient_company || null,
        recipient_email: formData.recipient_email || null,
        recipient_address: formData.recipient_address || null,
      };
      if (editingInvoice) {
        // Update existing invoice
        await axios.put(`${API}/invoices/${editingInvoice.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Invoice updated successfully!');
      } else {
        // Create new invoice
        await axios.post(`${API}/invoices`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Invoice created successfully!');
      }

      setOpen(false);
      setEditingInvoice(null);
      setIncludeVat(false); // Reset VAT checkbox
      setFormData({
        invoice_number: '',
        artist_id: '',
        invoice_to_type: 'artist',
        type: 'income',
        amount: '',
        currency: 'GBP',
        description: '',
        date: '',
        status: 'pending',
        from_entity_type: 'management',
        from_entity_id: '',
        recipient_name: '',
        recipient_company: '',
        recipient_email: '',
        recipient_address: '',
      });
      await fetchInvoices();
      await fetchSummary();
    } catch (error) {
      
      const errorDetail = error.response?.data?.detail;
      let errorMessage;
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        // Pydantic validation errors - extract field name and message
        errorMessage = errorDetail.map(e => {
          const field = e.loc ? e.loc.join('.') : '';
          const msg = e.msg || e.message || '';
          return field ? `${field}: ${msg}` : msg;
        }).join(', ');
      } else {
        errorMessage = editingInvoice ? 'Failed to update invoice' : 'Failed to create invoice';
      }
      toast.error(errorMessage);
    }
  };

  const handleEdit = (invoice) => {
    setEditingInvoice(invoice);
    
    // If invoice has VAT, use net_amount and enable VAT checkbox
    // This prevents the bug where VAT gets added on top of VAT when editing
    const hasVat = invoice.vat_included && invoice.net_amount;
    setIncludeVat(hasVat);
    
    setFormData({
      invoice_number: invoice.invoice_number,
      artist_id: invoice.artist_id || '',
      invoice_to_type: invoice.invoice_to_type || 'artist',
      type: invoice.type,
      // Use net_amount if VAT was included, otherwise use total amount
      amount: hasVat ? invoice.net_amount.toString() : invoice.amount.toString(),
      currency: invoice.currency || 'GBP',
      description: invoice.description,
      date: new Date(invoice.date).toISOString().slice(0, 10), // Format for date input
      status: invoice.status,
      from_entity_type: invoice.from_entity_type || 'management',
      from_entity_id: invoice.from_entity_id || user.id,
      recipient_name: invoice.recipient_name || '',
      recipient_company: invoice.recipient_company || '',
      recipient_email: invoice.recipient_email || '',
      recipient_address: invoice.recipient_address || '',
    });
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
    setEditingInvoice(null);
    setIncludeVat(false); // Reset VAT checkbox
    setFormData({
      invoice_number: '',
      artist_id: '',
      invoice_to_type: 'artist',
      type: 'income',
      amount: '',
      description: '',
      date: '',
      status: 'pending',
      from_entity_type: 'management',
      from_entity_id: '',
      recipient_name: '',
      recipient_company: '',
      recipient_email: '',
      recipient_address: '',
    });
  };

  const downloadInvoicePDF = async (invoiceId, invoiceNumber) => {
    const token = localStorage.getItem('token');
    const downloadUrl = `${API}/invoices/${invoiceId}/pdf/download?token=${encodeURIComponent(token)}`;
    
    // Create a hidden link and click it - more reliable across browsers including Safari
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Opening invoice PDF...');
  };

  // Generate a commission invoice for Underplay from an artist's outgoing invoice
  const generateCommissionInvoice = async (sourceInvoice) => {
    try {
      const token = localStorage.getItem('token');
      
      // Get artist settings to check VAT status
      let artistSettings = null;
      if (sourceInvoice.artist_id) {
        try {
          const settingsRes = await axios.get(`${API}/artist-settings/${sourceInvoice.artist_id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          artistSettings = settingsRes.data;
        } catch (e) {
          // Silent fail - artist settings is optional
        }
      }
      
      const commissionAmount = sourceInvoice.amount * 0.2;
      const hasVat = artistSettings?.vat_number ? true : false;
      const vatAmount = hasVat ? commissionAmount * 0.2 : 0;
      const totalAmount = commissionAmount + vatAmount;
      
      // Build confirmation message
      let confirmMsg = `Generate 20% commission invoice?\n\nCommission: £${commissionAmount.toFixed(2)}`;
      if (hasVat) {
        confirmMsg += `\nVAT (20%): £${vatAmount.toFixed(2)}\nTotal: £${totalAmount.toFixed(2)}`;
      }
      
      if (!window.confirm(confirmMsg)) {
        return;
      }
      
      // Generate invoice number
      const now = new Date();
      const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      
      // Build description with original invoice details
      let description = `20% Management Commission on ${sourceInvoice.invoice_number} (£${sourceInvoice.amount.toFixed(2)})`;
      if (hasVat) {
        description += `\nCommission: £${commissionAmount.toFixed(2)}\nVAT (20%): £${vatAmount.toFixed(2)}\nTotal: £${totalAmount.toFixed(2)}`;
      }
      // Include original invoice description/notes
      if (sourceInvoice.description) {
        description += `\n\nOriginal invoice: ${sourceInvoice.description}`;
      }
      
      const payload = {
        invoice_number: `COM-${dateStr}-${random}`,
        artist_id: sourceInvoice.artist_id,
        invoice_to_type: 'artist',
        type: 'income',
        amount: totalAmount,
        description: description,
        date: new Date().toISOString(),
        status: 'pending',
        from_entity_type: 'management',
        from_entity_id: user.id,
        recipient_name: '',
        recipient_company: '',
        recipient_email: '',
        recipient_address: '',
        source_invoice_id: sourceInvoice.id // Link to original invoice
      };
      
      await axios.post(`${API}/invoices`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      toast.success(`Commission invoice created: £${totalAmount.toFixed(2)}${hasVat ? ' (incl. VAT)' : ''}`);
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      
      toast.error('Failed to generate commission invoice');
    }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  const confirmDeleteInvoice = (invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteConfirmOpen(true);
  };

  const deleteInvoice = async () => {
    if (!invoiceToDelete) return;
    
    if (!isManager) {
      toast.error('Only managers can delete invoices');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/invoices/${invoiceToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Invoice moved to trash!');
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      await fetchInvoices();
      await fetchSummary();
      await fetchTrashedInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const handleBulkImportImage = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Image size should be less than 10MB');
        return;
      }
      setBulkImportImage(file);
    }
  };

  const handleBulkImportPreview = async () => {
    // Support both image and text import
    if (bulkImportTab === 'image' && !bulkImportImage) {
      toast.error('Please select an image first');
      return;
    }
    if (bulkImportTab === 'text' && !bulkImportText.trim()) {
      toast.error('Please paste some text content');
      return;
    }

    setBulkImporting(true);
    try {
      const token = localStorage.getItem('token');
      
      if (bulkImportTab === 'image') {
        // Image-based import
        const reader = new FileReader();
        reader.onload = async (event) => {
          const base64Data = event.target.result;
          
          toast.info('Extracting payment data from image...');
          
          const response = await axios.post(
            `${API}/invoices/bulk-import-preview`,
            {
              image_data: base64Data,
              payment_type: bulkImportType,
              commission_rate: commissionRate
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          setPreviewData(response.data);
          setShowPreview(true);
          toast.success('Data extracted! Please review before confirming.');
          setBulkImporting(false);
        };
        reader.readAsDataURL(bulkImportImage);
      } else {
        // Text-based import
        toast.info('Extracting payment data from text...');
        
        const response = await axios.post(
          `${API}/invoices/bulk-import-text-preview`,
          {
            text_content: bulkImportText,
            payment_type: bulkImportType,
            commission_rate: commissionRate
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setPreviewData(response.data);
        setShowPreview(true);
        toast.success('Data extracted! Please review before confirming.');
        setBulkImporting(false);
      }
    } catch (error) {
      
      toast.error(error.response?.data?.detail || 'Failed to extract data');
      setBulkImporting(false);
    }
  };

  const handleBulkImportConfirm = async () => {
    if (!previewData) return;

    setBulkImporting(true);
    try {
      const token = localStorage.getItem('token');
      
      toast.info('Creating and sending invoices...');
      
      const response = await axios.post(
        `${API}/invoices/bulk-import-confirm`,
        {
          preview_data: previewData,
          commission_rate: commissionRate,
          email_destination: emailDestination, // 'artist' or 'me'
          manager_email: user?.email // Send manager email for 'me' option
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const summary = response.data;
      const destText = emailDestination === 'me' ? ' (to your email)' : ' (to artists)';
      toast.success(
        `✅ Success! Created ${summary.invoices_created} invoice(s) and sent ${summary.emails_sent} email(s)${destText}!`,
        { duration: 5000 }
      );
      
      setBulkImportOpen(false);
      setBulkImportImage(null);
      setBulkImportText('');
      setPreviewData(null);
      setShowPreview(false);
      setEmailDestination('artist'); // Reset to default
      await fetchInvoices();
      await fetchSummary();
    } catch (error) {
      
      toast.error(error.response?.data?.detail || 'Failed to process import');
    } finally {
      setBulkImporting(false);
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    if (!isManager) {
      toast.error('Only managers can update invoice status');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/invoices/${invoiceId}/status`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Invoice status updated to ${newStatus}`);
      await fetchInvoices();
      await fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update invoice status');
    }
  };

  const getArtistName = (artistId) => {
    const artist = artists.find((a) => a.id === artistId);
    return artist ? artist.name : 'Unknown';
  };

  // Filter invoices based on status and source
  const filteredInvoices = invoices.filter(invoice => {
    // Search filter - search across invoice number, description, recipient name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const invoiceNumber = (invoice.invoice_number || '').toLowerCase();
      const description = (invoice.description || '').toLowerCase();
      const recipientName = (invoice.recipient_name || '').toLowerCase();
      const recipientCompany = (invoice.recipient_company || '').toLowerCase();
      
      if (!invoiceNumber.includes(query) && 
          !description.includes(query) && 
          !recipientName.includes(query) &&
          !recipientCompany.includes(query)) {
        return false;
      }
    }
    
    // Status filter
    if (statusFilter !== 'all' && invoice.status !== statusFilter) {
      return false;
    }
    
    // Source/type filter
    if (sourceFilter !== 'all') {
      const invoiceNumber = invoice.invoice_number?.toUpperCase() || '';
      const description = invoice.description?.toUpperCase() || '';
      
      if (sourceFilter === 'prs' && !invoiceNumber.includes('PRS') && !description.includes('PRS')) {
        return false;
      }
      if (sourceFilter === 'ppl' && !invoiceNumber.includes('PPL') && !description.includes('PPL')) {
        return false;
      }
      if (sourceFilter === 'commission' && !invoiceNumber.includes('COM') && !description.includes('COMMISSION')) {
        return false;
      }
      if (sourceFilter === 'other') {
        if (invoiceNumber.includes('PRS') || invoiceNumber.includes('PPL') || invoiceNumber.includes('COM') ||
            description.includes('PRS') || description.includes('PPL') || description.includes('COMMISSION')) {
          return false;
        }
      }
    }
    
    return true;
  });

  const sendInvoiceEmail = async () => {
    if (!recipientEmail) {
      toast.error('Please enter recipient email');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/invoices/${selectedInvoice.id}/send-email`,
        { invoice_id: selectedInvoice.id, recipient_email: recipientEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Invoice email sent successfully!');
      setEmailDialogOpen(false);
      setRecipientEmail('');
      setSelectedInvoice(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const generateCommissionInvoices = async () => {
    if (!window.confirm('Generate commission invoices for all artists for last month?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/invoices/generate-commission`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchInvoices();
      fetchSummary();
    } catch (error) {
      toast.error('Failed to generate commission invoices');
    }
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="space-y-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Invoices</h1>
          <p className="text-muted-foreground text-base mt-2">Track income and expenses</p>
        </div>

        {/* Management view info banner */}
        {selectedArtist === 'management' && (
          <div className="p-4 bg-primary/10 border border-primary/30 rounded-xl">
            <p className="text-sm font-medium text-primary">
              🏢 Viewing Underplay Management finances - Income shows commission earned, Expenses show company costs
            </p>
          </div>
        )}
        
        {/* Search and Filters */}
        <div className="flex flex-wrap items-center gap-4 p-4 bg-card border border-border/50 rounded-xl">
          {/* Search */}
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search invoice number, description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-input/50 border-border rounded-lg text-sm h-9"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery('')}
                className="h-7 w-7 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 bg-input/50 border border-border text-foreground rounded-lg text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Type:</span>
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-1.5 bg-input/50 border border-border text-foreground rounded-lg text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
            >
              <option value="all">All</option>
              <option value="prs">PRS</option>
              <option value="ppl">PPL</option>
              <option value="commission">Commission</option>
              <option value="other">Other</option>
            </select>
          </div>
          <span className="text-xs text-muted-foreground ml-auto">
            Showing {filteredInvoices.length} of {invoices.length} invoices
          </span>
        </div>

        {/* Trash View Toggle */}
        {isManager && (
          <div className="flex items-center gap-2">
            <Button
              variant={viewingTrash ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setViewingTrash(!viewingTrash);
                if (!viewingTrash) fetchTrashedInvoices();
              }}
              className={viewingTrash ? "bg-orange-600 hover:bg-orange-700" : ""}
            >
              <Archive className="w-4 h-4 mr-2" />
              {viewingTrash ? `Viewing Trash (${trashedInvoices.length})` : `Trash (${trashedInvoices.length})`}
            </Button>
            {viewingTrash && (
              <span className="text-sm text-muted-foreground">
                Deleted invoices can be restored or permanently deleted
              </span>
            )}
          </div>
        )}

        {isManager && (
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const response = await axios.get(`${API}/export/invoices/csv`, {
                    headers: { Authorization: `Bearer ${token}` }
                  });
                  
                  // Get CSV text directly and create download
                  const csvContent = response.data;
                  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                  
                  // Create download link
                  const link = document.createElement('a');
                  const url = URL.createObjectURL(blob);
                  link.setAttribute('href', url);
                  link.setAttribute('download', 'invoices.csv');
                  link.style.visibility = 'hidden';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  
                  // Cleanup
                  setTimeout(() => URL.revokeObjectURL(url), 100);
                  toast.success('Invoices exported to CSV!');
                } catch (error) {
                  
                  toast.error('Failed to export CSV');
                }
              }}
              className="flex items-center gap-2"
            >
              <FileDown className="w-4 h-4" />
              Export CSV
            </Button>
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="flex items-center gap-2"
                  data-testid="bulk-download-button"
                >
                  <Download className="w-4 h-4" />
                  Bulk Download PDFs
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/50">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-2xl">Download Invoices as PDFs</DialogTitle>
                </DialogHeader>
                <BulkDownloadForm selectedArtist={selectedArtist} />
              </DialogContent>
            </Dialog>
            <Dialog open={bulkImportOpen} onOpenChange={setBulkImportOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Bulk PRS/PPL Import
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh]">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-2xl">
                    Bulk Import PRS/PPL Payments
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 overflow-y-auto max-h-[70vh] pr-2">
                  <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/30">
                    <p className="text-sm font-semibold mb-2">🎉 Automate Your PRS/PPL Invoicing!</p>
                    <p className="text-sm text-muted-foreground mb-2">
                      Upload an image or paste text showing artist payments and let AI do the rest:
                    </p>
                    <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                      <li>AI extracts artist names and amounts</li>
                      <li>Calculates {commissionRate}% commission for Underplay</li>
                      <li>Creates invoices FROM Underplay TO each artist</li>
                      <li>Emails invoices to artists automatically</li>
                      <li>For bands, emails to group email or splits to members</li>
                    </ul>
                  </div>

                  {/* Tab Buttons */}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={bulkImportTab === 'image' ? 'default' : 'outline'}
                      onClick={() => setBulkImportTab('image')}
                      className="flex-1"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      From Image
                    </Button>
                    <Button
                      type="button"
                      variant={bulkImportTab === 'text' ? 'default' : 'outline'}
                      onClick={() => setBulkImportTab('text')}
                      className="flex-1"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      Paste Text
                    </Button>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Payment Type</Label>
                      <select
                        value={bulkImportType}
                        onChange={(e) => setBulkImportType(e.target.value)}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        <option value="prs">PRS Royalties</option>
                        <option value="ppl">PPL Royalties</option>
                        <option value="other">Other Payment</option>
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>Commission Rate (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={commissionRate}
                        onChange={(e) => setCommissionRate(Number(e.target.value))}
                        className="bg-input/50 border-border rounded-xl"
                      />
                    </div>
                  </div>

                  {/* Image Tab Content */}
                  {bulkImportTab === 'image' && (
                    <>
                      <div className="space-y-2">
                        <Label>Upload Payment Statement Image</Label>
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleBulkImportImage}
                          className="bg-input/50 border-border rounded-xl"
                        />
                        {bulkImportImage && (
                          <p className="text-sm text-green-600 flex items-center gap-2">
                            ✓ Selected: {bulkImportImage.name} ({(bulkImportImage.size / 1024).toFixed(1)} KB)
                          </p>
                        )}
                      </div>

                      <div className="p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
                        <p className="text-xs text-yellow-600 dark:text-yellow-400">
                          <strong>💡 Tip:</strong> For best results, upload a clear image showing artist names and payment amounts (spreadsheet, statement, or list).
                        </p>
                      </div>
                    </>
                  )}

                  {/* Text Tab Content */}
                  {bulkImportTab === 'text' && (
                    <>
                      <div className="space-y-2">
                        <Label>Paste Payment Statement Text</Label>
                        <Textarea
                          value={bulkImportText}
                          onChange={(e) => setBulkImportText(e.target.value)}
                          placeholder="Paste your royalty statement text here...

Example:
Artist Name: £1,234.56
Another Artist: £567.89
Band Name: £2,345.67"
                          className="bg-input/50 border-border rounded-xl min-h-[150px] font-mono text-sm"
                        />
                        {bulkImportText.trim() && (
                          <p className="text-sm text-green-600 flex items-center gap-2">
                            ✓ Text content ready ({bulkImportText.length} characters)
                          </p>
                        )}
                      </div>

                      <div className="p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <p className="text-xs text-green-600 dark:text-green-400">
                          <strong>📧 Tip:</strong> You can paste directly from emails, spreadsheets, or any text showing artist names and payment amounts.
                        </p>
                      </div>
                    </>
                  )}

                  {!showPreview ? (
                    <Button
                      onClick={handleBulkImportPreview}
                      disabled={bulkImporting || (bulkImportTab === 'image' ? !bulkImportImage : !bulkImportText.trim())}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      {bulkImporting ? (
                        <>Extracting Data...</>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Extract & Preview Data
                        </>
                      )}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30">
                        <h4 className="font-semibold mb-3">📋 Extracted Payment Data</h4>
                        {previewData?.matched && previewData.matched.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-green-600">✅ Matched Artists ({previewData.matched.length}):</p>
                            <div className="space-y-2">
                              {previewData.matched.map((item, idx) => (
                                <div key={item.artist_id || item.artist_name} className="bg-card/50 p-3 rounded-lg">
                                  <div className="flex justify-between items-start gap-4">
                                    <div className="flex-1">
                                      <p className="font-semibold text-base">TO: {item.artist_name}</p>
                                      <p className="text-xs text-muted-foreground">{item.email}</p>
                                      {item.has_company && (
                                        <p className="text-xs text-primary italic">{item.company_name}</p>
                                      )}
                                      {/* Show itemized breakdown if multiple periods */}
                                      {item.items && item.items.length > 1 && (
                                        <div className="mt-2 pt-2 border-t border-border/30">
                                          <p className="text-xs font-medium text-muted-foreground mb-1">Breakdown:</p>
                                          {item.items.map((itm) => (
                                            <p key={itm.period} className="text-xs text-muted-foreground">
                                              • {itm.period}: £{itm.amount.toFixed(2)}
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <p className="text-sm text-muted-foreground">Total Received: £{item.amount.toFixed(2)}</p>
                                      <p className="text-sm text-muted-foreground">Commission: £{item.commission.toFixed(2)}</p>
                                      {item.has_company && (
                                        <>
                                          <p className="text-xs text-blue-600">+ VAT (20%): £{item.vat_amount.toFixed(2)}</p>
                                        </>
                                      )}
                                      <p className="font-semibold text-orange-600">Invoice Total: £{item.total_with_vat.toFixed(2)}</p>
                                    </div>
                                  </div>
                                  {/* VAT Mode Toggle */}
                                  <div className="mt-3 pt-3 border-t border-border/30">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs text-muted-foreground">VAT handling:</span>
                                      <div className="flex gap-1 bg-muted/30 rounded-lg p-0.5">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newMatched = [...previewData.matched];
                                            newMatched[idx] = {
                                              ...item,
                                              vat_mode: 'add_on_top',
                                              vat_amount: item.commission * 0.20,
                                              total_with_vat: item.commission + (item.commission * 0.20)
                                            };
                                            setPreviewData({ ...previewData, matched: newMatched });
                                          }}
                                          className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                            (item.vat_mode || 'add_on_top') === 'add_on_top'
                                              ? 'bg-primary text-white'
                                              : 'text-muted-foreground hover:text-foreground'
                                          }`}
                                        >
                                          Add VAT on top
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newMatched = [...previewData.matched];
                                            // VAT inclusive: commission already includes VAT
                                            // So net = commission / 1.20, VAT = commission - net
                                            const netAmount = item.commission / 1.20;
                                            const vatAmount = item.commission - netAmount;
                                            newMatched[idx] = {
                                              ...item,
                                              vat_mode: 'inclusive',
                                              vat_amount: vatAmount,
                                              total_with_vat: item.commission // Total stays the same
                                            };
                                            setPreviewData({ ...previewData, matched: newMatched });
                                          }}
                                          className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                            item.vat_mode === 'inclusive'
                                              ? 'bg-primary text-white'
                                              : 'text-muted-foreground hover:text-foreground'
                                          }`}
                                        >
                                          VAT inclusive
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const newMatched = [...previewData.matched];
                                            newMatched[idx] = {
                                              ...item,
                                              vat_mode: 'no_vat',
                                              vat_amount: 0,
                                              total_with_vat: item.commission
                                            };
                                            setPreviewData({ ...previewData, matched: newMatched });
                                          }}
                                          className={`px-2 py-1 text-xs rounded-md transition-colors ${
                                            item.vat_mode === 'no_vat'
                                              ? 'bg-primary text-white'
                                              : 'text-muted-foreground hover:text-foreground'
                                          }`}
                                        >
                                          No VAT
                                        </button>
                                      </div>
                                    </div>
                                    {item.vat_mode === 'inclusive' && (
                                      <p className="text-xs text-amber-600 mt-1">
                                        ⚠️ VAT of £{item.vat_amount.toFixed(2)} is included in the £{item.commission.toFixed(2)} commission
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {previewData?.unmatched && previewData.unmatched.length > 0 && (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-yellow-600">⚠️ Unmatched Names ({previewData.unmatched.length}):</p>
                            <div className="text-sm text-muted-foreground">
                              {previewData.unmatched.map((name) => (
                                <span key={name}>{name}, </span>
                              )).slice(0, -1)}
                              {previewData.unmatched.length > 0 && (
                                <span key={previewData.unmatched[previewData.unmatched.length - 1]}>
                                  {previewData.unmatched[previewData.unmatched.length - 1]}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-muted-foreground">These will not be included in the invoice creation.</p>
                          </div>
                        )}
                      </div>
                      
                      {/* Email Destination Option */}
                      <div className="p-4 rounded-xl bg-muted/30 border border-border/50 mb-4">
                        <p className="text-sm font-medium mb-3">Email invoices to:</p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => setEmailDestination('artist')}
                            className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${
                              emailDestination === 'artist'
                                ? 'border-primary bg-primary/10 text-foreground'
                                : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-lg">👤</span>
                              <span className="font-medium text-sm">Direct to Artist</span>
                              <span className="text-xs opacity-70">Send immediately to each artist</span>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => setEmailDestination('me')}
                            className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all ${
                              emailDestination === 'me'
                                ? 'border-primary bg-primary/10 text-foreground'
                                : 'border-border bg-card text-muted-foreground hover:border-primary/50'
                            }`}
                          >
                            <div className="flex flex-col items-center gap-1">
                              <span className="text-lg">📧</span>
                              <span className="font-medium text-sm">Send to Me First</span>
                              <span className="text-xs opacity-70">Review before forwarding</span>
                            </div>
                          </button>
                        </div>
                        {emailDestination === 'me' && (
                          <p className="text-xs text-amber-600 mt-2">
                            ⚠️ Invoices will be sent to your email ({user?.email}). You can forward to artists after review.
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowPreview(false);
                            setPreviewData(null);
                          }}
                          className="flex-1"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkImportConfirm}
                          disabled={bulkImporting || !previewData?.matched || previewData.matched.length === 0}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          {bulkImporting ? (
                            <>Creating Invoices...</>
                          ) : (
                            <>
                              ✅ Confirm & Send {previewData?.matched?.length || 0} Invoice(s)
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
                data-testid="add-invoice-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Invoice
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="font-manrope font-bold text-2xl">
                  {editingInvoice ? 'Edit Invoice' : 'Create Invoice'}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-2">
                <form onSubmit={handleSubmit} className="space-y-4 pb-4">
                <div className="space-y-2">
                  <Label htmlFor="invoice_number">Invoice Number</Label>
                  <div className="flex gap-2">
                    <Input
                      id="invoice_number"
                      data-testid="invoice-number-input"
                      placeholder="INV-001"
                      value={formData.invoice_number}
                      onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                      required
                      className="bg-input/50 border-border rounded-xl flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateInvoiceNumber}
                      className="shrink-0"
                      data-testid="regenerate-invoice-number"
                    >
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">Edit manually or click Generate for auto-number</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="invoice_to">Invoice To</Label>
                  <select
                    id="invoice_to"
                    data-testid="invoice-to-select"
                    value={
                      formData.invoice_to_type === 'external' ? 'external' :
                      formData.invoice_to_type === 'artist' ? formData.artist_id : 
                      'management'
                    }
                    onChange={(e) => {
                      if (e.target.value === 'management') {
                        setFormData({ 
                          ...formData, 
                          invoice_to_type: 'management',
                          artist_id: '',
                          recipient_name: '',
                          recipient_company: '',
                          recipient_email: '',
                          recipient_address: ''
                        });
                      } else if (e.target.value === 'external') {
                        setFormData({ 
                          ...formData, 
                          invoice_to_type: 'external'
                          // Keep artist_id - user can select related artist separately
                        });
                      } else {
                        setFormData({ 
                          ...formData, 
                          invoice_to_type: 'artist',
                          artist_id: e.target.value,
                          recipient_name: '',
                          recipient_company: '',
                          recipient_email: '',
                          recipient_address: ''
                        });
                      }
                    }}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    required
                  >
                    <option value="">Select recipient</option>
                    <option value="management">Underplay Music (Management)</option>
                    <option value="external">📧 External / Other (Custom)</option>
                    <optgroup label="Artists">
                      {artists.map((artist) => (
                        <option key={artist.id} value={artist.id}>
                          {artist.name}
                        </option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="text-xs text-muted-foreground">
                    Select who this invoice is being sent to
                  </p>
                </div>

                {/* External recipient fields */}
                {formData.invoice_to_type === 'external' && (
                  <div className="space-y-3 p-4 bg-muted/30 rounded-xl border border-border/50">
                    <p className="text-sm font-medium text-muted-foreground">External Recipient Details</p>
                    
                    {/* Artist selector for external invoices */}
                    <div className="space-y-1">
                      <Label htmlFor="related_artist" className="text-xs">Related Artist (Optional)</Label>
                      <select
                        id="related_artist"
                        value={formData.artist_id || ''}
                        onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                        className="w-full px-3 py-2 bg-input/50 border border-border text-foreground rounded-lg text-sm focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
                      >
                        <option value="">No specific artist</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-muted-foreground">Associate this invoice with an artist for reporting</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label htmlFor="recipient_name" className="text-xs">Contact Name *</Label>
                        <Input
                          id="recipient_name"
                          placeholder="John Smith"
                          value={formData.recipient_name}
                          onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                          className="bg-input/50 border-border rounded-lg h-9 text-sm"
                          required
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor="recipient_company" className="text-xs">Company Name</Label>
                        <Input
                          id="recipient_company"
                          placeholder="Company Ltd"
                          value={formData.recipient_company}
                          onChange={(e) => setFormData({ ...formData, recipient_company: e.target.value })}
                          className="bg-input/50 border-border rounded-lg h-9 text-sm"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="recipient_email" className="text-xs">Email Address *</Label>
                      <Input
                        id="recipient_email"
                        type="email"
                        placeholder="email@example.com"
                        value={formData.recipient_email}
                        onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                        className="bg-input/50 border-border rounded-lg h-9 text-sm"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="recipient_address" className="text-xs">Address</Label>
                      <Textarea
                        id="recipient_address"
                        placeholder="Street, City, Postcode"
                        value={formData.recipient_address}
                        onChange={(e) => setFormData({ ...formData, recipient_address: e.target.value })}
                        className="bg-input/50 border-border rounded-lg text-sm min-h-[60px]"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="from_entity">Invoice From</Label>
                  <select
                    id="from_entity"
                    data-testid="invoice-from-select"
                    value={formData.from_entity_type === 'management' ? 'management' : formData.from_entity_type === 'label' ? 'label' : formData.from_entity_id}
                    onChange={(e) => {
                      if (e.target.value === 'management') {
                        setFormData({ ...formData, from_entity_type: 'management', from_entity_id: user.id });
                      } else if (e.target.value === 'label') {
                        setFormData({ ...formData, from_entity_type: 'label', from_entity_id: user.id });
                      } else {
                        setFormData({ ...formData, from_entity_type: 'artist', from_entity_id: e.target.value });
                      }
                    }}
                    className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    required
                  >
                    <option value="management">From Management</option>
                    <option value="label">From Record Label</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        From {artist.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground">
                    {formData.from_entity_type === 'label' ? 'Uses Record Label company details' : 'Uses Management company details'}
                  </p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="amount">Amount {includeVat ? '(Net - VAT will be added)' : ''}</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      data-testid="invoice-amount-input"
                      placeholder="1000.00"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      required
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="currency">Currency</Label>
                    <select
                      id="currency"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="w-full px-3 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      {currencies.map((curr) => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* VAT Option - Show for any invoice with an amount */}
                {formData.amount && parseFloat(formData.amount) > 0 && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="checkbox"
                        id="includeVat"
                        checked={includeVat}
                        onChange={(e) => setIncludeVat(e.target.checked)}
                        className="rounded"
                      />
                      <label htmlFor="includeVat" className="text-sm font-medium">Add VAT (20%)</label>
                    </div>
                    {includeVat && (
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>Net: {getCurrencySymbol(formData.currency)}{parseFloat(formData.amount || 0).toFixed(2)}</p>
                        <p>VAT (20%): {getCurrencySymbol(formData.currency)}{(parseFloat(formData.amount || 0) * 0.2).toFixed(2)}</p>
                        <p className="font-semibold text-foreground">Total: {getCurrencySymbol(formData.currency)}{(parseFloat(formData.amount || 0) * 1.2).toFixed(2)}</p>
                      </div>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    data-testid="invoice-date-input"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    className="bg-input/50 border-border rounded-xl"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    data-testid="invoice-description-input"
                    placeholder="Payment for services..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="bg-input/50 border-border rounded-xl min-h-24"
                    required
                  />
                </div>

                {/* Invoice "To:" Section */}
                <div className="border-t border-border/50 pt-4 space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground">INVOICE TO:</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="recipient_company">Company Name</Label>
                    <Input
                      id="recipient_company"
                      data-testid="invoice-recipient-company-input"
                      placeholder="Client Company Ltd"
                      value={formData.recipient_company || ''}
                      onChange={(e) => setFormData({ ...formData, recipient_company: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipient_name">Contact Person</Label>
                    <Input
                      id="recipient_name"
                      data-testid="invoice-recipient-name-input"
                      placeholder="John Smith"
                      value={formData.recipient_name || ''}
                      onChange={(e) => setFormData({ ...formData, recipient_name: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipient_email">Email</Label>
                    <Input
                      id="recipient_email"
                      type="email"
                      data-testid="invoice-recipient-email-input"
                      placeholder="client@example.com"
                      value={formData.recipient_email || ''}
                      onChange={(e) => setFormData({ ...formData, recipient_email: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="recipient_address">Address</Label>
                    <Textarea
                      id="recipient_address"
                      data-testid="invoice-recipient-address-input"
                      placeholder="123 Business Street&#10;London, UK&#10;W1A 1AA"
                      value={formData.recipient_address || ''}
                      onChange={(e) => setFormData({ ...formData, recipient_address: e.target.value })}
                      className="bg-input/50 border-border rounded-xl min-h-20"
                    />
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-invoice-button"
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    {editingInvoice ? 'Update Invoice' : 'Create Invoice'}
                  </Button>
                </div>
              </form>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-card border-border/50 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-500/20 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Income</p>
              <p className="font-manrope font-bold text-2xl text-foreground">£{summary.total_income.toFixed(2)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-red-500/20 flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Expenses</p>
              <p className="font-manrope font-bold text-2xl text-foreground">£{summary.total_expenses.toFixed(2)}</p>
            </div>
          </div>
        </Card>

        <Card className="bg-card border-border/50 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center">
              <PoundSterling className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Balance</p>
              <p className={`font-manrope font-bold text-2xl ${summary.balance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                £{summary.balance.toFixed(2)}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* Invoices List */}
      <div className="grid gap-4">
        {viewingTrash ? (
          // Trash View
          trashedInvoices.length === 0 ? (
            <Card className="bg-card border-border/50 p-12 text-center">
              <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">Trash is empty</p>
            </Card>
          ) : (
            trashedInvoices.map((invoice) => (
              <Card
                key={invoice.id}
                className="bg-muted/30 border-orange-500/30 p-6 opacity-80"
                data-testid={`trashed-invoice-${invoice.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-orange-500/20 flex items-center justify-center">
                      <Trash2 className="w-6 h-6 text-orange-500" />
                    </div>
                    <div>
                      <h3 className="font-manrope font-bold text-lg text-foreground">{invoice.invoice_number}</h3>
                      <p className="text-sm text-muted-foreground">
                        {invoice.description} • {getCurrencySymbol(invoice.currency)}{invoice.amount.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Deleted {invoice.deleted_at ? new Date(invoice.deleted_at).toLocaleDateString() : 'recently'}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => restoreInvoice(invoice.id)}
                      className="text-green-600 border-green-600/50 hover:bg-green-600/10"
                    >
                      <RotateCcw className="w-4 h-4 mr-1" />
                      Restore
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => permanentlyDeleteInvoice(invoice.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete Forever
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )
        ) : (
          // Regular Invoice View
          filteredInvoices.length === 0 ? (
            <Card className="bg-card border-border/50 p-12 text-center">
              <p className="text-muted-foreground">No invoices yet. {isManager ? 'Create your first invoice!' : ''}</p>
            </Card>
          ) : (
          filteredInvoices.map((invoice) => (
            <Card
              key={invoice.id}
              className="bg-card border-border/50 p-6 hover:border-primary/50 transition-colors"
              data-testid={`invoice-item-${invoice.id}`}
            >
              <div className="flex flex-col">
                {/* Header with Edit button */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        getEffectiveType(invoice) === 'income' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {getEffectiveType(invoice) === 'income' ? (
                          <TrendingUp className="w-6 h-6 text-green-500" />
                        ) : (
                          <TrendingDown className="w-6 h-6 text-red-500" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-manrope font-bold text-xl text-foreground">
                            {invoice.invoice_number}
                          </h3>
                          {isManager ? (
                            <Select
                              value={invoice.status}
                              onValueChange={(value) => updateInvoiceStatus(invoice.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                                    Pending
                                  </span>
                                </SelectItem>
                                <SelectItem value="paid">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    Paid
                                  </span>
                                </SelectItem>
                                <SelectItem value="overdue">
                                  <span className="flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-red-500"></div>
                                    Overdue
                                  </span>
                                </SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                              invoice.status === 'overdue' ? 'bg-red-500/20 text-red-500' :
                              'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {invoice.status}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {isManager ? getArtistName(invoice.artist_id) : 'Your Invoice'}
                        </p>
                      </div>
                    </div>
                  </div>
                  {isManager && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(invoice)}
                      className="min-w-[70px] min-h-[36px] touch-none"
                      data-testid={`edit-invoice-${invoice.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Edit
                    </Button>
                  )}
                </div>

                {/* Content */}
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-foreground">{invoice.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                      <span>{new Date(invoice.date).toLocaleDateString()}</span>
                      <span>•</span>
                      <div className="text-primary font-mono text-base">
                        {getCurrencySymbol(invoice.currency || 'GBP')}{invoice.amount.toFixed(2)}
                        {invoice.currency && invoice.currency !== 'GBP' && (
                          <span className="text-xs ml-1 text-muted-foreground">({invoice.currency})</span>
                        )}
                        {invoice.vat_included && (
                          <span className="text-xs ml-2 text-amber-600 dark:text-amber-400">(inc. VAT)</span>
                        )}
                      </div>
                    </div>
                    {invoice.vat_included && invoice.net_amount && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        Net: {getCurrencySymbol(invoice.currency || 'GBP')}{invoice.net_amount.toFixed(2)} + VAT: {getCurrencySymbol(invoice.currency || 'GBP')}{invoice.vat_amount?.toFixed(2)}
                      </div>
                    )}
                    {isManager && (
                      <div className="mt-2">
                        <span className="text-xs bg-purple-500/10 text-purple-600 dark:text-purple-400 px-2 py-1 rounded">
                          From: {invoice.from_entity_type === 'management' 
                            ? `Management (${companySettings?.company_name || 'Underplay Music Ltd'})` 
                            : invoice.from_entity_type === 'label'
                            ? `Record Label (${companySettings?.label_company_name || 'Underplay Recordings'})`
                            : getArtistName(invoice.from_entity_id || invoice.artist_id)}
                        </span>
                      </div>
                    )}
                  </div>
                  {isManager && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setPreviewDialogOpen(true);
                        }}
                        data-testid={`preview-invoice-${invoice.id}`}
                        className="flex items-center gap-2"
                      >
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => downloadInvoicePDF(invoice.id, invoice.invoice_number)}
                        data-testid={`download-pdf-${invoice.id}`}
                        className="flex items-center gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Download PDF
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedInvoice(invoice);
                          setEmailDialogOpen(true);
                        }}
                        data-testid={`email-invoice-${invoice.id}`}
                        className="flex items-center gap-2"
                      >
                        <Mail className="w-4 h-4" />
                        Send Email
                      </Button>
                      {/* Generate Commission button - only for artist outgoing invoices */}
                      {invoice.from_entity_type === 'artist' && invoice.invoice_to_type === 'external' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => generateCommissionInvoice(invoice)}
                          data-testid={`commission-invoice-${invoice.id}`}
                          className="flex items-center gap-2 border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
                        >
                          <Percent className="w-4 h-4" />
                          +20% Commission
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Delete button at bottom with separation */}
                {isManager && (
                  <div className="mt-6 pt-4 border-t border-border/30">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => confirmDeleteInvoice(invoice)}
                      className="w-full min-h-[36px] touch-none"
                      data-testid={`delete-invoice-${invoice.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete Invoice
                    </Button>
                  </div>
                )}
              </div>
            </Card>
          ))
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-card border-border/50 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-xl text-red-500 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Delete Invoice?
            </DialogTitle>
          </DialogHeader>
          {invoiceToDelete && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
                <p className="font-semibold text-lg">{invoiceToDelete.invoice_number}</p>
                <p className="text-sm text-muted-foreground mt-1">{invoiceToDelete.description}</p>
                <p className="text-lg font-bold mt-2">
                  {getCurrencySymbol(invoiceToDelete.currency || 'GBP')}{invoiceToDelete.amount?.toFixed(2)}
                </p>
              </div>
              <p className="text-sm text-muted-foreground">
                This invoice will be moved to trash. You can restore it later if needed.
              </p>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeleteConfirmOpen(false);
                    setInvoiceToDelete(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={deleteInvoice}
                  className="flex-1"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Move to Trash
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Email Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent className="bg-card border-border/50">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-2xl">Send Invoice via Email</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-accent/20 border border-border/50">
                <p className="text-sm text-muted-foreground mb-1">Invoice Number</p>
                <p className="font-manrope font-bold text-lg">{selectedInvoice.invoice_number}</p>
                <p className="text-sm text-muted-foreground mt-2">Amount: £{selectedInvoice.amount.toFixed(2)}</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient-email">Recipient Email</Label>
                <Input
                  id="recipient-email"
                  type="email"
                  data-testid="recipient-email-input"
                  placeholder="client@example.com"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  className="bg-input/50 border-border rounded-xl"
                />
              </div>
              <Button
                onClick={sendInvoiceEmail}
                data-testid="send-email-button"
                className="w-full bg-primary hover:bg-primary/90"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={async (open) => {
        if (open && selectedInvoice) {
          // Fetch artist settings when opening preview
          const artistSettings = await fetchSettingsForArtist(selectedInvoice.artist_id);
          if (artistSettings) {
            setCompanySettings(artistSettings);
          }
        }
        setPreviewDialogOpen(open);
      }}>
        <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-2xl">Invoice Preview</DialogTitle>
          </DialogHeader>
          {selectedInvoice && (
            <div className="space-y-6">
              {/* Header */}
              <div className="border-b border-border/50 pb-6">
                <h1 className="font-manrope font-extrabold text-3xl text-primary mb-3">
                  {companySettings?.company_name || getArtistName(selectedInvoice.artist_id)}
                </h1>
                {companySettings && (
                  <div className="text-sm text-muted-foreground space-y-1">
                    {companySettings.address_line1 && <p>{companySettings.address_line1}</p>}
                    {companySettings.address_line2 && <p>{companySettings.address_line2}</p>}
                    {(companySettings.city || companySettings.postcode) && (
                      <p>{companySettings.city}{companySettings.city && companySettings.postcode ? ', ' : ''}{companySettings.postcode}</p>
                    )}
                    {companySettings.country && <p>{companySettings.country}</p>}
                    {companySettings.vat_number && <p className="mt-2">VAT: {companySettings.vat_number}</p>}
                    {companySettings.email && <p>Email: {companySettings.email}</p>}
                    {companySettings.phone && <p>Phone: {companySettings.phone}</p>}
                  </div>
                )}
              </div>

              {/* Invoice Details */}
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Invoice Number</p>
                  <p className="font-manrope font-bold text-lg">{selectedInvoice.invoice_number}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Date</p>
                  <p className="font-manrope font-semibold">{new Date(selectedInvoice.date).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Artist Info */}
              {isManager && (
                <div className="p-4 rounded-xl bg-accent/20 border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Artist</p>
                  <p className="font-manrope font-semibold">{getArtistName(selectedInvoice.artist_id)}</p>
                </div>
              )}

              {/* Description */}
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Description</p>
                <p className="text-sm text-foreground bg-accent/10 p-4 rounded-xl">
                  {/* Clean description - remove VAT breakdown if it's a commission invoice */}
                  {selectedInvoice.description
                    .replace(/\nCommission: £[\d.]+\nVAT \(20%\): £[\d.]+\nTotal: £[\d.]+/g, '')
                    .replace(/\n\nOriginal invoice:/g, '\n\nRe:')}
                </p>
              </div>

              {/* Line items for commission invoices */}
              {selectedInvoice.description.includes('Commission: £') && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Charges</p>
                  <div className="bg-accent/10 rounded-xl overflow-hidden">
                    <div className="flex justify-between p-3 border-b border-border/30">
                      <span className="text-sm">Management Commission (20%)</span>
                      <span className="text-sm font-semibold">
                        £{(selectedInvoice.description.match(/Commission: £([\d.]+)/)?.[1] || '0.00')}
                      </span>
                    </div>
                    <div className="flex justify-between p-3">
                      <span className="text-sm">VAT (20%)</span>
                      <span className="text-sm font-semibold">
                        £{(selectedInvoice.description.match(/VAT \(20%\): £([\d.]+)/)?.[1] || '0.00')}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Amount */}
              <div className="p-6 rounded-xl bg-primary/10 border-2 border-primary/30 text-center">
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Total Amount</p>
                <p className="font-manrope font-extrabold text-4xl text-primary">£{selectedInvoice.amount.toFixed(2)}</p>
              </div>

              {/* Payment Details */}
              {companySettings && (companySettings.bank_name || companySettings.payment_notes) && (
                <div className="p-4 rounded-xl bg-accent/20 border border-border/50">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Payment Details</p>
                  {companySettings.bank_name && (
                    <div className="text-sm space-y-1">
                      <p className="text-foreground"><span className="text-muted-foreground">Bank:</span> {companySettings.bank_name}</p>
                      {companySettings.sort_code && <p className="text-foreground"><span className="text-muted-foreground">Sort Code:</span> {companySettings.sort_code}</p>}
                      {companySettings.account_number && <p className="text-foreground"><span className="text-muted-foreground">Account Number:</span> {companySettings.account_number}</p>}
                    </div>
                  )}
                  {companySettings.payment_notes && (
                    <p className="text-sm text-muted-foreground mt-3 italic">{companySettings.payment_notes}</p>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPreviewDialogOpen(false)}
                  className="flex-1"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setPreviewDialogOpen(false);
                    setEmailDialogOpen(true);
                  }}
                  className="flex-1 bg-primary hover:bg-primary/90"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send via Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Invoices;